import { addNewUserValidations } from './validations';
import Input from 'src/components/forms/input';
import { IUser } from '../../../../../../../utils/interfaces/user';
import Spinner from 'src/components/ui/spinner/index';
import { useFormik } from 'formik';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import {
    DealerManagementService,
    IAddFieldUserPayload,
    IUpdateFieldUserPayload,
} from 'src/services/dealer-management.service';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import Accordion from './Components/Accordion';
import { IUserData } from '../Body';
import { NUMBER_ZERO } from 'src/utils/constants';
import './styles.scss';

interface IFormProps {
    setSuccessNotification: Dispatch<SetStateAction<boolean>>;
    setErrorNotification: Dispatch<SetStateAction<boolean>>;
    setSuccessNotificationMessage: Dispatch<SetStateAction<string>>;
    setErrorNotificationMessage: Dispatch<SetStateAction<string>>;
    onUserUpdate: Function;
    userData: IUserData | null;
    regions: Array<any>;
    districts: Array<any>;
    isLoadingDistAndReg: boolean;
    isUserActive: boolean;
    isEditMode?: boolean;
}

const Form: React.FC<IFormProps> = ({
    setSuccessNotification,
    setErrorNotification,
    setSuccessNotificationMessage,
    setErrorNotificationMessage,
    onUserUpdate,
    userData,
    regions,
    districts,
    isLoadingDistAndReg,
    isUserActive,
    isEditMode,
}) => {
    const { dealerDataInfo } = useSelector((state: IUser) => {
        return state;
    });
    const brand: string = dealerDataInfo?.dealer.brand || 'vw';
    const [submitErrors, setSubmitErrors] = useState({
        email: '',
        phone: '',
        firstName: '',
        lastName: '',
    });
    const [selectedDistricts, setSelectedDistricts] = useState<Array<string>>(
        [],
    );
    const [selectedRegions, setSelectedRegions] = useState<Array<string>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const {
        values,
        errors,
        touched,
        resetForm,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldValue,
    } = useFormik({
        initialValues: {
            email: userData?.email || '',
            phone: userData ? addHyphens(userData.phone) : '',
            firstName: userData?.firstName || '',
            lastName: userData?.lastName || '',
        },
        enableReinitialize: true,
        validationSchema: addNewUserValidations,
        onSubmit: handlerSubmit,
    });
    const { addNewUser } = useLocalization();

    async function handlerSubmit(): Promise<void> {
        setLoading(true);
        setSuccessNotification(false);
        setErrorNotification(false);
        setSuccessNotificationMessage('');
        setErrorNotificationMessage('');
        setSubmitErrors({
            email: '',
            phone: '',
            firstName: '',
            lastName: '',
        });

        const service = new DealerManagementService();

        try {
            if (isEditMode) {
                const request = {
                    firstName: values.firstName.trim(),
                    lastName: values.lastName.trim(),
                    emailAddress: values.email.toLowerCase(),
                    currentEmailAddress: userData?.email,
                    userId: userData?.userId,
                    phoneNumber: values.phone.replace(/\D/g, ''),
                    country: dealerDataInfo?.dealer.country,
                    newDistricts: selectedDistricts.map((dist) => {
                        return { districtId: Number(dist) };
                    }),
                    newRegions: selectedRegions.map((reg) => {
                        return { regionId: Number(reg) };
                    }),
                };
                await service.updateFieldUser(
                    request as IUpdateFieldUserPayload,
                );
                onUserUpdate();
            } else {
                const request = {
                    firstName: values.firstName.trim(),
                    lastName: values.lastName.trim(),
                    emailAddress: values.email.toLowerCase(),
                    phoneNumber: values.phone.replace(/\D/g, ''),
                    country: dealerDataInfo?.dealer.country,
                    districts: selectedDistricts.map((dist) => {
                        return { districtId: Number(dist) };
                    }),
                    regions: selectedRegions.map((reg) => {
                        return { regionId: Number(reg) };
                    }),
                };
                await service.addFieldUser(request as IAddFieldUserPayload);
                resetForm();
                setSelectedDistricts([]);
                setSelectedRegions([]);
            }
            setLoading(false);
            setSuccessNotification(true);
        } catch (error) {
            setLoading(false);
            setErrorNotification(true);
        }
    }

    const onChangePhoneHandler = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const formattedPhone = addHyphens(event.target.value);
        setFieldValue(event.target.name, formattedPhone);
    };

    useEffect(() => {
        const initialRegions = userData
            ? userData.regions.map((e) => e.regionId)
            : [];
        const initialDistricts = userData
            ? userData.districts.map((e) => e.districtId)
            : [];
        setSelectedRegions(initialRegions);
        setSelectedDistricts(initialDistricts);
    }, [userData]);

    const findArrayDiff = (a: any, b: any) => {
        const diff = [
            ...a.filter((x: any) => !b.includes(x)),
            ...b.filter((x: any) => !a.includes(x)),
        ];

        return !!diff.length;
    };

    const isEdited = (): boolean => {
        if (!isEditMode) return true;
        const userRegions = userData
            ? userData.regions.map((e) => e.regionId)
            : [];
        const userDistricts = userData
            ? userData.districts.map((e) => e.districtId)
            : [];

        const regionDiff = findArrayDiff(userRegions, selectedRegions);
        const districtDiff = findArrayDiff(userDistricts, selectedDistricts);
        const res =
            values.email !== userData?.email ||
            values.firstName !== userData?.firstName ||
            values.lastName !== userData?.lastName ||
            values.phone.replace(/\D/g, '') !==
                userData?.phone.replace(/\D/g, '') ||
            regionDiff ||
            districtDiff;

        return res;
    };

    const onDiscardHanlder = (event: any) => {
        event.preventDefault();
        if (isEditMode && userData) {
            values.email = userData.email;
            values.firstName = userData.firstName;
            values.lastName = userData.lastName;
            values.phone = addHyphens(userData.phone);
            const initialRegions = userData
                ? userData.regions.map((e) => e.regionId)
                : [];
            const initialDistricts = userData
                ? userData.districts.map((e) => e.districtId)
                : [];
            setSelectedRegions(initialRegions);
            setSelectedDistricts(initialDistricts);
        } else {
            values.email = '';
            values.firstName = '';
            values.lastName = '';
            values.phone = '';
            setSelectedRegions([]);
            setSelectedDistricts([]);
        }
    };

    return (
        <form
            className={'field-user-container__body--form'}
            onSubmit={handleSubmit}
        >
            <div className='name-fields-wrapper'>
                <Input
                    labelName={addNewUser.FORM_LABEL_FIRSTNAME}
                    type={'text'}
                    name={'firstName'}
                    maxlength={30}
                    handlerChange={handleChange}
                    handlerBlur={handleBlur}
                    value={values.firstName}
                    asErrors={errors.firstName}
                    asTouched={touched.firstName}
                    asSubmitError={submitErrors.firstName}
                    disabled={isEditMode && !isUserActive}
                />
                <Input
                    labelName={addNewUser.FORM_LABEL_LASTNAME}
                    type={'text'}
                    name={'lastName'}
                    maxlength={30}
                    handlerChange={handleChange}
                    handlerBlur={handleBlur}
                    value={values.lastName}
                    asErrors={errors.lastName}
                    asTouched={touched.lastName}
                    asSubmitError={submitErrors.lastName}
                    disabled={isEditMode && !isUserActive}
                />
            </div>
            <Input
                labelName={addNewUser.FORM_LABEL_PHONE}
                type={'text'}
                name={'phone'}
                maxlength={12}
                handlerChange={onChangePhoneHandler}
                handlerBlur={handleBlur}
                value={values.phone}
                asErrors={errors.phone}
                asTouched={touched.phone}
                asSubmitError={submitErrors.phone}
                handleKeyPress={onKeyPressPhoneHandler}
                disabled={isEditMode && !isUserActive}
            />
            <Input
                labelName={addNewUser.FORM_LABEL_EMAIL}
                type={'text'}
                name={'email'}
                maxlength={50}
                handlerChange={handleChange}
                handlerBlur={handleBlur}
                value={values.email}
                asErrors={errors.email}
                asTouched={touched.email}
                asSubmitError={submitErrors.email}
                disabled={isEditMode && !isUserActive}
            />
            <div>
                <h4>Dealership Associations</h4>
                <p>
                    Select the dealers this user can access.
                    <br />
                    <strong>Note:</strong> Users cannot be granted access to
                    both US and Canadian regions.
                </p>
            </div>
            <Accordion
                label='Select Regions'
                title='Regions'
                subtitle='Select the region(s) to grant access. Choosing a region grants access to all dealerships within it.'
                options={regions}
                selectedOptions={selectedRegions}
                setSelectedOptions={setSelectedRegions}
                valueProp='regionCode'
                nameProp='regionName'
                disabled={isEditMode && !isUserActive}
                isOpenDefault
                isLoading={isLoadingDistAndReg}
            />
            <Accordion
                label='Select Field Districts'
                title='Field Districts'
                subtitle='Select the district(s) to grant access. Choosing a district grants access to all dealerships within it.'
                options={districts}
                selectedOptions={selectedDistricts}
                setSelectedOptions={setSelectedDistricts}
                valueProp='districtCode'
                nameProp='districtName'
                disabled={isEditMode && !isUserActive}
                isOpenDefault
                isLoading={isLoadingDistAndReg}
                displayCheckboxInRow
            />
            <div className='field-user--form_button-ct'>
                <button
                    className={`edit-user--form_button secondary c-btn ${brand.toLowerCase()}`}
                    disabled={
                        values.email === '' ||
                        values.firstName === '' ||
                        values.lastName === '' ||
                        values.phone === '' ||
                        !!Object.keys(errors).length ||
                        selectedDistricts.length === NUMBER_ZERO ||
                        selectedRegions.length === NUMBER_ZERO ||
                        loading ||
                        !isEdited()
                    }
                    type={'button'}
                    onClick={onDiscardHanlder}
                >
                    <span className='c-btn__text'>Cancel</span>
                </button>
                <button
                    className={`field-user--form_button c-btn ${brand.toLowerCase()}`}
                    disabled={
                        values.email === '' ||
                        values.firstName === '' ||
                        values.lastName === '' ||
                        values.phone === '' ||
                        !!Object.keys(errors).length ||
                        selectedDistricts.length === NUMBER_ZERO ||
                        selectedRegions.length === NUMBER_ZERO ||
                        loading ||
                        !isEdited()
                    }
                    type={'submit'}
                >
                    <span className='c-btn__text'>
                        {loading ? (
                            <Spinner />
                        ) : isEditMode ? (
                            'Save'
                        ) : (
                            'Add User'
                        )}
                    </span>
                </button>
            </div>
        </form>
    );
};

export function addHyphens(phone: string) {
    phone = phone.replace(/\D/g, '');

    phone = phone.replace(
        /^(\d{3})(\d{1,3})?(\d{1,4})?/,
        (match, p1, p2, p3) => {
            let result = p1;
            if (p2) {
                result += '-' + p2;
            }
            if (p3) {
                result += '-' + p3;
            }

            return result;
        },
    );

    return phone;
}

export const onKeyPressPhoneHandler = (event: any) => {
    const charCode = event.which ? event.which : event.keyCode;
    const backspaceKeyCode = 8;
    const tabKeyCode = 9;
    const deleteKeyCode = 46;
    const firstNumberKeyCode = 48;
    const lastNumberKeyCode = 57;
    const firstNumPadKeyCode = 96;
    const lastNumPadKeyCode = 105;

    if (
        charCode == backspaceKeyCode ||
        charCode == deleteKeyCode ||
        charCode == tabKeyCode
    ) {
        return true;
    }

    if (
        charCode < firstNumberKeyCode ||
        (charCode > lastNumberKeyCode && charCode < firstNumPadKeyCode) ||
        charCode > lastNumPadKeyCode
    )
        event.preventDefault();
};

export default Form;
